import type {
  OperatorIdentity,
  ServiceDutyReadModel,
  ServiceRoutingConfigurationReadModel,
  ServiceRoutingRole,
  ServiceWorkItemReadModel,
} from "@billiard/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  PackageCheck,
  RefreshCw,
  Save,
  UserRoundCheck,
  XCircle,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  ApiRequestError,
  acknowledgeKioskProductOrder,
  assertOperatorIdentityUnchanged,
  assumeServiceDuty,
  cancelKioskProductOrder,
  currentOperatorIdentityEpoch,
  deliverKioskProductOrder,
  getServiceDuty,
  getServiceRouting,
  releaseServiceDuty,
  updateServiceRouting,
} from "./api";
import {
  beginServiceDutyMutationDispatch,
  claimServiceDutyMutationRecovery,
  clearServiceDutyMutationRecovery,
  finishServiceDutyMutationDispatch,
  markServiceDutyMutationOutcomeUnknown,
  readServiceDutyMutationRecovery,
  type ServiceDutyMutationCommand,
  type ServiceDutyMutationRecovery,
  type ServiceDutyMutationRecoveryView,
  serviceDutyMutationIdentityKey,
  serviceDutyMutationOutcomeIsUnknown,
} from "./serviceDutyMutationRecovery";

type OperatorRole = OperatorIdentity["role"];

const routingRoleLabels: Record<ServiceRoutingRole, string> = {
  MANAGER: "店长",
  CASHIER: "收银员",
  WAITER: "服务员",
};

interface ServiceRoutingFormDraft {
  primaryRole: ServiceRoutingRole;
  copiedRoles: ServiceRoutingRole[];
  serviceSlaMinutes: string;
  checkoutSlaMinutes: string;
  ownerEscalationDelayMinutes: string;
  allowCashierTemporaryDuty: boolean;
}

function parsedSlaMinutes(value: string): number | null {
  if (!/^[0-9]+$/.test(value)) return null;
  const minutes = Number(value);
  return Number.isSafeInteger(minutes) && minutes >= 1 && minutes <= 1_440 ? minutes : null;
}

export function isPersonalCashier(
  effectiveRoles: readonly OperatorRole[],
  personalSession: boolean,
): boolean {
  return personalSession && effectiveRoles.includes("CASHIER");
}

export function serviceDutyQueryKey(operator: OperatorIdentity): readonly unknown[] {
  return ["service-duty", operator.tenantId, operator.storeId, operator.id] as const;
}

export function serviceWorkItemsQueryKey(operator: OperatorIdentity): readonly unknown[] {
  return ["service-work-items", operator.tenantId, operator.storeId, operator.id] as const;
}

export function serviceRoutingQueryKey(operator: OperatorIdentity): readonly unknown[] {
  return ["service-routing", operator.tenantId, operator.storeId, operator.id] as const;
}

function commandLabel(command: ServiceDutyMutationCommand): string {
  if (command.kind === "UPDATE_ROUTING") return "保存现场职责设置";
  if (command.kind === "ASSUME") return "临时承担服务职责";
  if (command.kind === "RELEASE") return "退出临时服务职责";
  if (command.kind === "ACKNOWLEDGE_KIOSK_PRODUCT_ORDER") return "接下桌边商品送达";
  if (command.kind === "DELIVER_KIOSK_PRODUCT_ORDER") return "确认桌边商品送达";
  return "拒绝桌边商品并释放预留";
}

function isKioskProductOrderCommand(
  command: ServiceDutyMutationCommand | null | undefined,
): boolean {
  return Boolean(command?.kind.endsWith("_KIOSK_PRODUCT_ORDER"));
}

export function kioskProductOrderFailureMessage(error: Error): string {
  if (!(error instanceof ApiRequestError)) return "本次操作结果无法确认，请使用原请求安全恢复。";
  if (error.code.includes("VALUATION") || error.code.includes("INVENTORY_VALUE")) {
    return "库存价值无法安全确认，本次没有加入账单、没有扣减库存。请店长先核对商品库存价值，再处理这笔送达。";
  }
  if (error.code.includes("INVENTORY")) {
    return "当前可用库存不足或商品预留已经变化，本次没有加入账单、没有扣减库存。请刷新后核对现货和预留。";
  }
  if (
    error.code.includes("VERSION_CONFLICT") ||
    error.code.includes("STATE_INVALID") ||
    error.code.includes("SESSION_NOT_OPEN") ||
    error.code.includes("PRODUCT_VERSION_CHANGED")
  ) {
    return "球台、商品或送达状态已经变化，本次没有写入。请刷新现场工作台后按最新状态处理。";
  }
  if (
    error.statusCode === 403 ||
    error.code === "FORBIDDEN" ||
    error.code === "SERVICE_DUTY_NOT_ACTIVE"
  ) {
    return "当前账号没有执行这一步的权限。本次没有写入；请由已接单员工送达，或由店长处理拒绝。";
  }
  return error.message;
}

function useServiceDutyMutation(operator: OperatorIdentity): {
  view: ServiceDutyMutationRecoveryView;
  pending: boolean;
  error: Error | null;
  message: string;
  lastCommand: ServiceDutyMutationCommand | null;
  submit: (command: ServiceDutyMutationCommand) => void;
  retry: () => void;
} {
  const queryClient = useQueryClient();
  const identityKey = serviceDutyMutationIdentityKey(operator);
  const identityKeyRef = useRef(identityKey);
  identityKeyRef.current = identityKey;
  const [viewState, setViewState] = useState<{
    identityKey: string;
    view: ServiceDutyMutationRecoveryView;
  }>(() => ({ identityKey, view: readServiceDutyMutationRecovery(identityKey) }));
  const [message, setMessage] = useState("");
  const view =
    viewState.identityKey === identityKey
      ? viewState.view
      : ({ status: "OTHER_OPERATOR" } as const);

  const mutation = useMutation({
    mutationFn: async (input: {
      recovery: ServiceDutyMutationRecovery;
      identityKey: string;
      identityEpoch: number;
    }) => {
      const { recovery } = input;
      try {
        if (identityKeyRef.current !== input.identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的现场职责操作仍待确认",
          );
        }
        assertOperatorIdentityUnchanged(input.identityEpoch);
        const result =
          recovery.command.kind === "UPDATE_ROUTING"
            ? await updateServiceRouting(recovery.command.request)
            : recovery.command.kind === "ASSUME"
              ? await assumeServiceDuty(recovery.command.request)
              : recovery.command.kind === "RELEASE"
                ? await releaseServiceDuty(recovery.command.request)
                : recovery.command.kind === "ACKNOWLEDGE_KIOSK_PRODUCT_ORDER"
                  ? await acknowledgeKioskProductOrder(recovery.command.request)
                  : recovery.command.kind === "DELIVER_KIOSK_PRODUCT_ORDER"
                    ? await deliverKioskProductOrder(recovery.command.request)
                    : await cancelKioskProductOrder(recovery.command.request);
        assertOperatorIdentityUnchanged(input.identityEpoch);
        if (identityKeyRef.current !== input.identityKey) {
          throw new ApiRequestError(
            "OPERATOR_IDENTITY_CHANGED",
            "账号已切换；原账号的现场职责操作仍待确认",
          );
        }
        if (!clearServiceDutyMutationRecovery(recovery, input.identityKey)) {
          throw new ApiRequestError(
            "SERVICE_DUTY_RECOVERY_CLEAR_FAILED",
            "服务端已返回结果，但本机安全记录无法清除；请勿重复操作",
          );
        }
        return result;
      } catch (error) {
        if (serviceDutyMutationOutcomeIsUnknown(error, recovery.state)) {
          markServiceDutyMutationOutcomeUnknown(recovery, input.identityKey);
        } else {
          clearServiceDutyMutationRecovery(recovery, input.identityKey);
        }
        throw error;
      } finally {
        finishServiceDutyMutationDispatch(recovery, input.identityKey);
      }
    },
    onSuccess: async (result, input) => {
      const kind = input.recovery.command.kind;
      setMessage(
        kind === "UPDATE_ROUTING"
          ? "现场职责设置已保存。当前所有未完成待办会立即按新规则重新分派，并从原发起时间重算时限；部分事项可能立即逾期或升级提醒。"
          : kind === "ASSUME"
            ? "你已临时承担服务职责，可以进入现场工作台接单。"
            : kind === "RELEASE"
              ? "你已安全退出临时服务职责。"
              : kind === "ACKNOWLEDGE_KIOSK_PRODUCT_ORDER"
                ? "桌边商品已接单。商品仍为顾客预留；确认送达前不会加入本桌账单，也不会扣减库存。"
                : kind === "DELIVER_KIOSK_PRODUCT_ORDER"
                  ? "送达已确认：商品已加入本桌账单并扣减库存。页面正在刷新权威账单和剩余库存。"
                  : "已拒绝这笔桌边商品请求并释放预留；没有加入本桌账单，也没有扣减库存。",
      );
      if (kind === "UPDATE_ROUTING") {
        queryClient.setQueryData(serviceRoutingQueryKey(operator), result);
      } else if (kind === "ASSUME" || kind === "RELEASE") {
        queryClient.setQueryData(serviceDutyQueryKey(operator), result);
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["service-duty"] }),
        queryClient.invalidateQueries({ queryKey: ["service-routing"] }),
        queryClient.invalidateQueries({ queryKey: ["service-work-items"] }),
        queryClient.invalidateQueries({ queryKey: ["products"] }),
        queryClient.invalidateQueries({ queryKey: ["inventory-products"] }),
        queryClient.invalidateQueries({ queryKey: ["orders"] }),
        queryClient.invalidateQueries({ queryKey: ["tables"] }),
      ]);
    },
    onSettled: (_data, _error, input) => {
      if (identityKeyRef.current !== input.identityKey) return;
      setViewState({
        identityKey: input.identityKey,
        view: readServiceDutyMutationRecovery(input.identityKey),
      });
    },
  });

  useEffect(() => {
    setViewState({ identityKey, view: readServiceDutyMutationRecovery(identityKey) });
    setMessage("");
  }, [identityKey]);

  const dispatch = (recovery: ServiceDutyMutationRecovery): void => {
    if (!beginServiceDutyMutationDispatch(recovery, identityKey)) {
      setViewState({ identityKey, view: readServiceDutyMutationRecovery(identityKey) });
      return;
    }
    mutation.mutate({
      recovery,
      identityKey,
      identityEpoch: currentOperatorIdentityEpoch(),
    });
  };

  const submit = (command: ServiceDutyMutationCommand): void => {
    setMessage("");
    const claimed = claimServiceDutyMutationRecovery(identityKey, command);
    setViewState({ identityKey, view: claimed });
    if (
      claimed.status === "OWNED" &&
      claimed.recovery.command.request.idempotencyKey === command.request.idempotencyKey
    ) {
      dispatch(claimed.recovery);
    }
  };

  return {
    view,
    pending: mutation.isPending,
    error: mutation.error instanceof Error ? mutation.error : null,
    message,
    lastCommand:
      mutation.variables?.identityKey === identityKey
        ? mutation.variables.recovery.command
        : view.status === "OWNED"
          ? view.recovery.command
          : null,
    submit,
    retry: () => {
      if (view.status === "OWNED") dispatch(view.recovery);
    },
  };
}

function RecoveryNotice({
  view,
  pending,
  error,
  command,
  onRetry,
}: {
  view: ServiceDutyMutationRecoveryView;
  pending: boolean;
  error: Error | null;
  command?: ServiceDutyMutationCommand | null;
  onRetry: () => void;
}): React.JSX.Element | null {
  if (view.status === "NONE") {
    if (!error) return null;
    return (
      <div className="error-band" role="alert">
        {isKioskProductOrderCommand(command)
          ? kioskProductOrderFailureMessage(error)
          : error.message}
      </div>
    );
  }
  if (view.status === "OWNED") {
    const submitting = view.recovery.state === "CLAIMED";
    const kioskProductOrder = isKioskProductOrderCommand(view.recovery.command);
    return (
      <section className="error-band service-duty-recovery" role={submitting ? "status" : "alert"}>
        <AlertTriangle size={18} aria-hidden="true" />
        <div>
          <strong>
            {submitting
              ? `正在${commandLabel(view.recovery.command)}`
              : `上次${commandLabel(view.recovery.command)}结果尚未确认`}
          </strong>
          <p>
            {kioskProductOrder
              ? "这笔商品操作的最终结果尚未确认。系统保留了原商品、数量、预留编号和原请求；不要另建操作，只能用原请求安全恢复。"
              : (error?.message ?? "系统保留了原请求；不要重新发起另一笔操作。")}
          </p>
        </div>
        {!submitting ? (
          <button
            className="button secondary compact"
            type="button"
            disabled={pending}
            onClick={onRetry}
          >
            <RefreshCw size={15} aria-hidden="true" /> 使用原请求安全重试
          </button>
        ) : null}
      </section>
    );
  }
  return (
    <div className="error-band" role="alert">
      {view.status === "OTHER_OPERATOR"
        ? "另一个账号有尚未确认的现场职责操作。请切回原账号处理，当前账号已暂停新操作。"
        : "无法安全保存现场职责操作进度。为避免重复操作，本页已暂停写入。"}
    </div>
  );
}

function RoutingSummary({
  configuration,
}: {
  configuration: ServiceRoutingConfigurationReadModel;
}) {
  const copied = configuration.copiedRoles.map((role) => routingRoleLabels[role]).join("、");
  return (
    <dl className="service-duty-routing-summary">
      <div>
        <dt>主要负责人</dt>
        <dd>{routingRoleLabels[configuration.primaryRole]}</dd>
      </div>
      <div>
        <dt>同步提醒</dt>
        <dd>{copied || "不额外提醒其他角色"}</dd>
      </div>
      <div>
        <dt>现场服务时限</dt>
        <dd>
          {configuration.serviceSlaMinutes === null
            ? "尚未设置"
            : `${configuration.serviceSlaMinutes} 分钟`}
        </dd>
      </div>
      <div>
        <dt>结账请求时限</dt>
        <dd>
          {configuration.checkoutSlaMinutes === null
            ? "尚未设置"
            : `${configuration.checkoutSlaMinutes} 分钟`}
        </dd>
      </div>
      <div>
        <dt>逾期后提醒老板</dt>
        <dd>
          {configuration.ownerEscalationDelayMinutes === null
            ? "尚未设置"
            : `${configuration.ownerEscalationDelayMinutes} 分钟`}
        </dd>
      </div>
    </dl>
  );
}

export function ServiceDutyWorkCenterPanel({
  operator,
  effectiveRoles,
  personalSession,
  simulated = false,
}: {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorRole[];
  personalSession: boolean;
  simulated?: boolean;
}): React.JSX.Element {
  const duty = useQuery({
    queryKey: serviceDutyQueryKey(operator),
    queryFn: getServiceDuty,
    refetchInterval: 2_000,
  });
  const mutation = useServiceDutyMutation(operator);
  const personalCashier = isPersonalCashier(effectiveRoles, personalSession);
  const hasTrustedData = duty.data !== undefined;
  return (
    <section className="service-duty-card" aria-label="现场服务职责">
      <div className="section-heading">
        <div>
          <h3>谁负责现场服务</h3>
          <p>主要负责人先处理，同步提醒只用于知情；设置处理时限后，逾期才会升级提醒。</p>
        </div>
        <button className="button secondary compact" type="button" onClick={() => duty.refetch()}>
          <RefreshCw size={15} aria-hidden="true" /> 刷新职责
        </button>
      </div>
      {duty.isError ? (
        <div className="error-band" role="alert">
          {hasTrustedData
            ? "现场职责更新失败，旧状态可能已经变化；已暂停承担或退出。"
            : "现场职责暂时无法读取，不能安全承担或退出。"}
        </div>
      ) : null}
      {simulated ? (
        <p className="field-help">演示账号：本处操作只用于内部 QA，并只影响演示数据。</p>
      ) : null}
      {!hasTrustedData ? (
        <div className="empty-state">
          {duty.isLoading ? "正在读取现场职责" : "暂无可信职责状态"}
        </div>
      ) : (
        <>
          <RoutingSummary configuration={duty.data.configuration} />
          {duty.data.configuration.serviceSlaMinutes === null ? (
            <p className="field-help">处理时限尚未配置，当前不会显示倒计时、逾期或升级提醒。</p>
          ) : null}
          {duty.data.status === "ACTIVE" ? (
            <p className="field-help">
              当前由 {duty.data.currentAssignee?.displayName ?? "负责人待核对"} 承担服务职责。
            </p>
          ) : (
            <p className="field-help">当前没有收银员临时承担服务职责。</p>
          )}
          {personalCashier ? (
            duty.data.currentAssignee?.isCurrentActor ? (
              <div className="service-duty-current-actions">
                <p className="field-help">
                  你已接下 {duty.data.incompleteClaimedCount} 项尚未完成的现场工作。
                </p>
                <NavLink className="button primary" to="/staff">
                  进入现场工作台
                </NavLink>
                <button
                  className="button secondary"
                  type="button"
                  disabled={
                    !duty.data.canRelease || duty.isError || mutation.view.status !== "NONE"
                  }
                  title={duty.data.releaseUnavailableReason ?? "确认已接事项全部完成后退出"}
                  onClick={() =>
                    mutation.submit({
                      kind: "RELEASE",
                      request: {
                        expectedDutyVersion: duty.data.dutyVersion,
                        idempotencyKey: crypto.randomUUID(),
                      },
                    })
                  }
                >
                  退出临时服务职责
                </button>
                {!duty.data.canRelease ? (
                  <p className="field-help">{duty.data.releaseUnavailableReason}</p>
                ) : null}
              </div>
            ) : (
              <button
                className="button primary"
                type="button"
                disabled={!duty.data.canAssume || duty.isError || mutation.view.status !== "NONE"}
                title={duty.data.assumeUnavailableReason ?? "临时承担后可以接收和完成现场服务"}
                onClick={() =>
                  mutation.submit({
                    kind: "ASSUME",
                    request: {
                      expectedConfigVersion: duty.data.configuration.configVersion,
                      expectedDutyVersion: duty.data.dutyVersion,
                      idempotencyKey: crypto.randomUUID(),
                    },
                  })
                }
              >
                <UserRoundCheck size={17} aria-hidden="true" /> 临时承担服务职责
              </button>
            )
          ) : null}
          {personalCashier && !duty.data.canAssume && !duty.data.currentAssignee?.isCurrentActor ? (
            <p className="field-help">{duty.data.assumeUnavailableReason}</p>
          ) : null}
        </>
      )}
      <RecoveryNotice
        view={mutation.view}
        pending={mutation.pending}
        error={mutation.error}
        command={mutation.lastCommand}
        onRetry={mutation.retry}
      />
      {mutation.message ? <output className="success-band">{mutation.message}</output> : null}
    </section>
  );
}

export function ServiceDutyStaffPanel({
  operator,
  duty,
  isError,
  simulated = false,
}: {
  operator: OperatorIdentity;
  duty: ServiceDutyReadModel | undefined;
  isError: boolean;
  simulated?: boolean;
}): React.JSX.Element {
  const mutation = useServiceDutyMutation(operator);
  return (
    <section className="service-duty-card" aria-label="临时服务职责状态">
      <div>
        <strong>临时服务职责</strong>
        <p>
          {duty?.currentAssignee?.isCurrentActor
            ? `当前由你承担；还有 ${duty.incompleteClaimedCount} 项已接服务未完成。`
            : "当前账号没有承担临时服务职责，只能查看待办。"}
        </p>
      </div>
      {simulated ? <p className="field-help">演示账号：现场职责和待办只影响演示数据。</p> : null}
      {isError ? <div className="error-band">职责状态更新失败，已暂停退出操作。</div> : null}
      {duty?.currentAssignee?.isCurrentActor ? (
        <button
          className="button secondary"
          type="button"
          disabled={!duty.canRelease || isError || mutation.view.status !== "NONE"}
          title={duty.releaseUnavailableReason ?? "确认所有已接事项完成后退出"}
          onClick={() =>
            mutation.submit({
              kind: "RELEASE",
              request: {
                expectedDutyVersion: duty.dutyVersion,
                idempotencyKey: crypto.randomUUID(),
              },
            })
          }
        >
          退出临时服务职责
        </button>
      ) : null}
      {duty?.currentAssignee?.isCurrentActor && !duty.canRelease ? (
        <p className="field-help">{duty.releaseUnavailableReason}</p>
      ) : null}
      <RecoveryNotice
        view={mutation.view}
        pending={mutation.pending}
        error={mutation.error}
        command={mutation.lastCommand}
        onRetry={mutation.retry}
      />
      {mutation.message ? <output className="success-band">{mutation.message}</output> : null}
    </section>
  );
}

export type KioskProductOrderWorkItem = ServiceWorkItemReadModel & {
  source: Extract<ServiceWorkItemReadModel["source"], { kind: "KIOSK_PRODUCT_ORDER" }>;
};

function formatKioskProductMoney(fen: number): string {
  const value = BigInt(fen);
  const yuan = value / 100n;
  const remainder = (value % 100n).toString().padStart(2, "0");
  return `¥${yuan}.${remainder}`;
}

function KioskProductOrderWorkItemCard({
  item,
  effectiveRoles,
  disabled,
  mutation,
}: {
  item: KioskProductOrderWorkItem;
  effectiveRoles: readonly OperatorRole[];
  disabled: boolean;
  mutation: ReturnType<typeof useServiceDutyMutation>;
}): React.JSX.Element {
  const [reason, setReason] = useState("");
  const staffRole = effectiveRoles.some(
    (role) => role === "WAITER" || role === "CASHIER" || role === "MANAGER",
  );
  const manager = effectiveRoles.includes("MANAGER");
  const canAcknowledge =
    item.source.state === "PENDING" && item.viewerActions.canAcknowledge && staffRole;
  const canDeliver =
    item.source.state === "ACKNOWLEDGED" && item.viewerActions.canComplete && staffRole;
  const canCancel =
    manager && (item.source.state === "PENDING" || item.source.state === "ACKNOWLEDGED");
  const trimmedReason = reason.trim();
  const actionBlocked = disabled || mutation.pending || mutation.view.status !== "NONE";
  const submitTransition = (
    kind: "ACKNOWLEDGE_KIOSK_PRODUCT_ORDER" | "DELIVER_KIOSK_PRODUCT_ORDER",
  ): void => {
    mutation.submit({
      kind,
      request: {
        reservationId: item.source.reservationId,
        expectedReservationVersion: item.source.reservationVersion,
        idempotencyKey: crypto.randomUUID(),
      },
    });
  };
  return (
    <article
      className="staff-task product-delivery"
      aria-label={`${item.tableNumber} 号台桌边商品`}
    >
      <div className="staff-task-heading">
        <span className="task-icon service">
          <PackageCheck aria-hidden="true" />
        </span>
        <div>
          <strong>{item.tableNumber} 号台 · 桌边商品送达</strong>
          <small>{item.statusLabel}</small>
        </div>
      </div>
      <dl className="approval-card-facts">
        <div>
          <dt>商品</dt>
          <dd>{item.source.productName}</dd>
        </div>
        <div>
          <dt>数量</dt>
          <dd>
            {item.source.quantity} {item.source.unit}
          </dd>
        </div>
        <div>
          <dt>顾客下单时售价</dt>
          <dd>
            {formatKioskProductMoney(item.source.unitPriceFen)} / {item.source.unit}
          </dd>
        </div>
        <div>
          <dt>本次合计</dt>
          <dd>{formatKioskProductMoney(item.source.lineTotalFen)}</dd>
        </div>
        <div>
          <dt>负责人</dt>
          <dd>{item.assigneeDisplayName ?? "尚未接单"}</dd>
        </div>
      </dl>
      <p>{item.statusExplanation}</p>
      <p className="field-help">
        商品已按顾客下单时售价预留。接单只确认由谁送达；确认送达后才会加入本桌账单并扣减库存；拒绝会释放预留。
      </p>
      {canAcknowledge ? (
        <button
          className="button primary compact"
          type="button"
          disabled={actionBlocked}
          onClick={() => submitTransition("ACKNOWLEDGE_KIOSK_PRODUCT_ORDER")}
        >
          <UserRoundCheck size={15} aria-hidden="true" /> 接下并准备送达
        </button>
      ) : null}
      {canDeliver ? (
        <button
          className="button primary compact"
          type="button"
          disabled={actionBlocked}
          onClick={() => submitTransition("DELIVER_KIOSK_PRODUCT_ORDER")}
        >
          <CheckCircle2 size={15} aria-hidden="true" /> 确认已送达
        </button>
      ) : null}
      {canCancel ? (
        <div className="service-duty-current-actions">
          <label>
            拒绝原因（必填）
            <input
              type="text"
              maxLength={300}
              value={reason}
              disabled={actionBlocked}
              placeholder="例如：商品已售罄，已向顾客说明"
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          <p className="field-help">
            拒绝只会取消本次商品请求并释放预留，不会加入本桌账单，也不会扣减库存。
          </p>
          <button
            className="button secondary compact"
            type="button"
            disabled={actionBlocked || trimmedReason.length === 0}
            onClick={() =>
              mutation.submit({
                kind: "CANCEL_KIOSK_PRODUCT_ORDER",
                request: {
                  reservationId: item.source.reservationId,
                  expectedReservationVersion: item.source.reservationVersion,
                  idempotencyKey: crypto.randomUUID(),
                  reason: trimmedReason,
                },
              })
            }
          >
            <XCircle size={15} aria-hidden="true" /> 拒绝并释放预留
          </button>
        </div>
      ) : null}
      {!canAcknowledge && !canDeliver && !canCancel ? (
        <p className="field-help">
          {staffRole ? item.viewerActions.unavailableReason : "当前账号只能查看这笔桌边商品请求。"}
        </p>
      ) : null}
    </article>
  );
}

export function KioskProductOrderWorkItemPanel({
  operator,
  effectiveRoles,
  items,
  disabled = false,
}: {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorRole[];
  items: readonly ServiceWorkItemReadModel[];
  disabled?: boolean;
}): React.JSX.Element | null {
  const productOrders = items.filter(
    (item): item is KioskProductOrderWorkItem => item.source.kind === "KIOSK_PRODUCT_ORDER",
  );
  const mutation = useServiceDutyMutation(operator);
  if (productOrders.length === 0 && mutation.view.status === "NONE" && !mutation.message) {
    return null;
  }
  return (
    <section className="staff-task-list kiosk-product-order-list" aria-label="桌边商品送达待办">
      <div className="section-heading">
        <div>
          <span className="eyebrow">桌边商品送达</span>
          <h2>核对商品和冻结售价后送到球台</h2>
          <p>接单不会立即记账；只有确认送达成功，商品才会加入本桌账单并扣减库存。</p>
        </div>
      </div>
      {disabled ? (
        <div className="error-band" role="alert">
          桌边商品待办更新失败，旧状态可能已经变化；已暂停接单、送达和拒绝，请先刷新。
        </div>
      ) : null}
      <RecoveryNotice
        view={mutation.view}
        pending={mutation.pending}
        error={mutation.error}
        command={mutation.lastCommand}
        onRetry={mutation.retry}
      />
      {mutation.message ? <output className="success-band">{mutation.message}</output> : null}
      {productOrders.map((item) => (
        <KioskProductOrderWorkItemCard
          key={item.workItemId}
          item={item}
          effectiveRoles={effectiveRoles}
          disabled={disabled}
          mutation={mutation}
        />
      ))}
    </section>
  );
}

export function ServiceRoutingConfigurationPanel({
  operator,
  canEdit,
}: {
  operator: OperatorIdentity;
  canEdit: boolean;
}): React.JSX.Element {
  const routing = useQuery({
    queryKey: serviceRoutingQueryKey(operator),
    queryFn: getServiceRouting,
  });
  const mutation = useServiceDutyMutation(operator);
  const [draft, setDraft] = useState<ServiceRoutingFormDraft | null>(null);
  useEffect(() => {
    if (!routing.data || mutation.view.status === "OWNED") return;
    setDraft({
      primaryRole: routing.data.primaryRole,
      copiedRoles: [...routing.data.copiedRoles],
      serviceSlaMinutes: routing.data.serviceSlaMinutes?.toString() ?? "",
      checkoutSlaMinutes: routing.data.checkoutSlaMinutes?.toString() ?? "",
      ownerEscalationDelayMinutes: routing.data.ownerEscalationDelayMinutes?.toString() ?? "",
      allowCashierTemporaryDuty: routing.data.allowCashierTemporaryDuty,
    });
  }, [routing.data, mutation.view.status]);
  if (routing.isLoading && !routing.data) {
    return <section className="configuration-loading">正在读取现场职责设置</section>;
  }
  if (!routing.data || !draft) {
    return (
      <section className="configuration-section">
        <div className="error-band">现场职责设置无法安全读取，旧内容已隐藏。</div>
        <button className="button secondary" type="button" onClick={() => routing.refetch()}>
          重新读取
        </button>
      </section>
    );
  }
  const roles: readonly ServiceRoutingRole[] = ["WAITER", "CASHIER", "MANAGER"];
  const serviceSlaMinutes = parsedSlaMinutes(draft.serviceSlaMinutes);
  const checkoutSlaMinutes = parsedSlaMinutes(draft.checkoutSlaMinutes);
  const ownerEscalationDelayMinutes = parsedSlaMinutes(draft.ownerEscalationDelayMinutes);
  const slaIsComplete =
    serviceSlaMinutes !== null &&
    checkoutSlaMinutes !== null &&
    ownerEscalationDelayMinutes !== null;
  return (
    <section
      className="configuration-section service-routing-configuration"
      aria-label="现场服务职责设置"
    >
      <div className="section-heading">
        <div>
          <h2>现场服务职责</h2>
          <p>主要负责人先接单；同步提醒角色只收到提醒，不会自动成为负责人。</p>
        </div>
      </div>
      {!canEdit ? (
        <p className="configuration-impact-note">
          老板可查看当前门店的分派规则；本店规则由店长修改，避免跨店误改。
        </p>
      ) : null}
      <div className="configuration-grid three-columns">
        <label>
          主要负责人
          <select
            value={draft.primaryRole}
            disabled={!canEdit || mutation.view.status !== "NONE"}
            onChange={(event) => {
              const primaryRole = event.currentTarget.value as ServiceRoutingRole;
              setDraft(
                (current) =>
                  current && {
                    ...current,
                    primaryRole,
                    copiedRoles: current.copiedRoles.filter((role) => role !== primaryRole),
                    allowCashierTemporaryDuty:
                      primaryRole === "CASHIER" ? true : current.allowCashierTemporaryDuty,
                  },
              );
            }}
          >
            {roles.map((role) => (
              <option key={role} value={role}>
                {routingRoleLabels[role]}
              </option>
            ))}
          </select>
          <span className="field-help">保存后，当前未完成事项和新请求都会交给这个岗位。</span>
        </label>
        <label>
          现场服务处理时限（分钟）
          <input
            disabled={!canEdit}
            type="number"
            min={1}
            max={1440}
            value={draft.serviceSlaMinutes}
            onChange={(event) => {
              const value = event.currentTarget.value;
              setDraft((current) => current && { ...current, serviceSlaMinutes: value });
            }}
          />
          <span className="field-help">超过这个时间仍未完成，会标为逾期。</span>
        </label>
        <label>
          结账请求处理时限（分钟）
          <input
            disabled={!canEdit}
            type="number"
            min={1}
            max={1440}
            value={draft.checkoutSlaMinutes}
            onChange={(event) => {
              const value = event.currentTarget.value;
              setDraft((current) => current && { ...current, checkoutSlaMinutes: value });
            }}
          />
          <span className="field-help">顾客请求结账后，应在这个时间内响应。</span>
        </label>
        <label>
          逾期多久再提醒老板（分钟）
          <input
            disabled={!canEdit}
            type="number"
            min={1}
            max={1440}
            value={draft.ownerEscalationDelayMinutes}
            onChange={(event) => {
              const value = event.currentTarget.value;
              setDraft((current) => current && { ...current, ownerEscalationDelayMinutes: value });
            }}
          />
          <span className="field-help">先提醒店长；继续超过这段时间，再提醒老板。</span>
        </label>
      </div>
      <fieldset>
        <legend>同步提醒给谁</legend>
        {roles
          .filter((role) => role !== draft.primaryRole)
          .map((role) => (
            <label className="switch-label" key={role}>
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={draft.copiedRoles.includes(role)}
                onChange={(event) => {
                  const checked = event.currentTarget.checked;
                  setDraft(
                    (current) =>
                      current && {
                        ...current,
                        copiedRoles: checked
                          ? [...current.copiedRoles, role]
                          : current.copiedRoles.filter((candidate) => candidate !== role),
                      },
                  );
                }}
              />
              {routingRoleLabels[role]}
            </label>
          ))}
      </fieldset>
      <label className="switch-label">
        <input
          type="checkbox"
          disabled={!canEdit || draft.primaryRole === "CASHIER"}
          checked={draft.allowCashierTemporaryDuty}
          onChange={(event) => {
            const checked = event.currentTarget.checked;
            setDraft((current) => current && { ...current, allowCashierTemporaryDuty: checked });
          }}
        />
        没有专职服务员时，允许收银员临时承担服务职责
      </label>
      {draft.primaryRole === "CASHIER" ? (
        <p className="field-help">收银员是主要负责人时，此开关必须启用，否则没有人能接下新请求。</p>
      ) : null}
      {canEdit && !slaIsComplete ? (
        <p className="configuration-field-error" role="alert">
          请明确填写三项处理时间，每项为 1 到 1440 分钟；系统不会替你猜默认值。
        </p>
      ) : null}
      <p className="configuration-impact-note">
        保存后，所有未完成事项会立即重新分派，并从原发起时间重算时限，可能马上变成逾期或升级提醒。启用收银兜底后，收银员仍需主动承担，不会自动增加资金权限。
      </p>
      {canEdit ? (
        <button
          className="button primary"
          type="button"
          disabled={routing.isError || mutation.view.status !== "NONE" || !slaIsComplete}
          onClick={() =>
            mutation.submit({
              kind: "UPDATE_ROUTING",
              request: {
                expectedConfigVersion: routing.data.configVersion,
                primaryRole: draft.primaryRole,
                copiedRoles: draft.copiedRoles,
                serviceSlaMinutes: serviceSlaMinutes as number,
                checkoutSlaMinutes: checkoutSlaMinutes as number,
                ownerEscalationDelayMinutes: ownerEscalationDelayMinutes as number,
                allowCashierTemporaryDuty: draft.allowCashierTemporaryDuty,
                idempotencyKey: crypto.randomUUID(),
              },
            })
          }
        >
          <Save size={17} aria-hidden="true" /> 保存现场职责设置
        </button>
      ) : null}
      <RecoveryNotice
        view={mutation.view}
        pending={mutation.pending}
        error={mutation.error}
        onRetry={mutation.retry}
      />
      {mutation.message ? <output className="success-band">{mutation.message}</output> : null}
    </section>
  );
}
